<script lang="ts">
	import { getTranslate } from "@utils/getTranslate.js";
	import { slide } from "svelte/transition";
	import leaf from "@core/assets/icons/leaf.svg?raw";
	import { SubscriptionFrequency } from "@core/schema/SubscriptionFrequency.js";
	import { SubscriptionSize } from "@core/schema/SubscriptionSize.js";
	import { subscriptionSizeNameMap } from "@core/schema/names/subscriptionSizeNameMap.js";
	import { subscriptionFrequencyToWeeksMap } from "@core/schema/utils/subscriptionFrequencyToWeeksMap.js";
	import { subscriptionSizeToAmountMap } from "@core/schema/utils/subscriptionSizeToAmountMap.js";
	import { subscriptionSizeToWeightMap } from "@core/schema/utils/subscriptionSizeToWeightMap.js";
	import { StorageState } from "@core/utils/svelte/StorageState.svelte.js";
	import { Cart } from "../cart/Cart.svelte.js";
	import { CartStep } from "../cart/CartStep.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./EcoFriendlyTip.translations.json" with { type: "json" };
	import Button from "./common/Button.svelte";
	import { ButtonSize } from "./common/ButtonSize.js";
	import { ButtonVariant } from "./common/ButtonVariant.js";
	import Formatted from "./common/Formatted.svelte";
	import Icon from "./common/Icon.svelte";

	const { tenant } = getGlobalTemplateProps();
	const translate = getTranslate(translations);
	const rejected = new StorageState("session", "ecoFriendlyTipRejected", false);
	const cart = $derived(Cart.getCorrectCartInStepOrThrow(CartStep.PaymentDelivery));

	const amount = $derived(subscriptionSizeToAmountMap[cart.subscription.size]);
	const weight = $derived(subscriptionSizeToWeightMap[cart.subscription.size]);
	const weeks = $derived(subscriptionFrequencyToWeeksMap[cart.subscription.frequency]);

	const size = $derived.by(() => {
		if (amount !== 1) {
			return undefined;
		}

		return Object.values(SubscriptionSize).find(
			(size) => subscriptionSizeToAmountMap[size] !== 1 && subscriptionSizeToWeightMap[size] === weight,
		);
	});

	const frequency = $derived.by(() => {
		if (size === undefined) {
			return undefined;
		}

		return Object.values(SubscriptionFrequency).find(
			(frequency) => subscriptionFrequencyToWeeksMap[frequency] === weeks * subscriptionSizeToAmountMap[size],
		);
	});
</script>

{#if !rejected.value && size !== undefined && frequency !== undefined}
	<div
		class="bg-fcrbeige border border-fcrlightgray rounded-xl overflow-hidden shrink-0"
		transition:slide={{ duration: 150 }}
	>
		<div class="flex px-3.5 gap-3.5 py-3 bg-fcrsuccess/15">
			<div class="flex rounded-full bg-fcrsuccess size-6.5" aria-hidden="true">
				<Icon icon={leaf} class="size-4 mt-1 ml-1.5 text-white" />
			</div>
			<div class="flex flex-col gap-1">
				<div class="text-xs/normal text-fcrsuccess uppercase font-semibold tracking-[0.125em]">
					<Formatted text={translate("heading")} />
				</div>

				<div class="text-xs/none">
					<Formatted
						text={translate("label", {
							name: subscriptionSizeNameMap[size][tenant.locale],
							weeks: subscriptionFrequencyToWeeksMap[frequency].toString(),
						})}
					/>
				</div>
				<div class="flex gap-4 mt-1.5">
					<Button
						variant={ButtonVariant.Dark}
						size={ButtonSize.Custom}
						class="!bg-fcrsuccess py-3 px-3.5 text-xs/none"
						onclick={(): void => {
							Cart.saveSubscriptionFields({ frequency, size });
						}}
					>
						<Formatted text={translate("useRecommendation")} />
					</Button>
					<button
						type="button"
						class="text-xs/none text-fcrgray font-semibold"
						onclick={(): void => {
							rejected.value = true;
						}}
					>
						<Formatted text={translate("noThanks")} />
					</button>
				</div>
			</div>
		</div>
	</div>
{/if}
